// CanvasHud — the movable container holding the SearchBar + zoom
// controls on the canvas page. Both share one drag-able panel so the
// admin can park it wherever they like and have search + zoom move
// together. Position persists in localStorage (see useFloatingHudPosition).
//
// Why both in one panel: the user explicitly asked for the search
// (with its Cmd+K hint) AND the zoom controls to live inside the same
// movable container. Splitting them into two HUDs is trivial later if
// the requirement reverses — the FloatingHud primitive is reusable.

import { FloatingHud } from '@/components/ui/FloatingHud';
import { SearchBar } from '@/components/search/SearchBar';
import { CanvasScaleControls } from '@/gui/workflow/CanvasScaleControls';

export interface CanvasHudProps {
  /** Current canvas zoom (0..1+) — used for the percent readout. */
  contentScale: number;
  /** Effective zoom range for this tier. */
  minScale: number;
  maxScale: number;
  /** Tier-resolved zoom mode (static / dynamic) — drives the tooltip. */
  zoomMode?: 'static' | 'dynamic';
  /** Zoom handlers from WorkflowCanvas. */
  onScaleUp: () => void;
  onScaleDown: () => void;
  onResetScale: () => void;
  onFitToScreen?: () => void;
}

export function CanvasHud(props: CanvasHudProps) {
  return (
    <FloatingHud
      id="canvas-search-zoom"
      // Default placement: top-right, matching the legacy GlobalSearchMount
      // anchor + the legacy CanvasHeader zoom-controls slot.
      defaultPosition={{ x: typeof window === 'undefined' ? 1000 : Math.max(8, window.innerWidth - 360), y: 12 }}
      title="Sök · Zoom"
      approxSize={{ width: 350, height: 140 }}
      showResetButton
      className="w-[340px] max-w-[calc(100vw-16px)]"
    >
      <div className="space-y-2">
        <SearchBar />
        <div className="flex justify-center">
          <CanvasScaleControls
            contentScale={props.contentScale}
            minScale={props.minScale}
            maxScale={props.maxScale}
            zoomMode={props.zoomMode}
            onScaleUp={props.onScaleUp}
            onScaleDown={props.onScaleDown}
            onResetScale={props.onResetScale}
            onFitToScreen={props.onFitToScreen}
          />
        </div>
      </div>
    </FloatingHud>
  );
}
